/**
 * Vilken sorts enhet och webbläsare det är, och var man slår på
 * platsåtkomst där.
 *
 * Instruktionerna skiljer sig helt mellan iOS och Android, och ett råd som
 * gäller fel plattform är sämre än inget råd — man letar efter en meny som
 * inte finns.
 */
export function detectPlatform() {
  const ua = navigator.userAgent || '';

  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

/** Är sidan inbäddad i en annan apps webbläsare? Där blockeras plats ofta. */
export function isInAppBrowser() {
  const ua = navigator.userAgent || '';
  return /FBAN|FBAV|Instagram|Snapchat|TikTok|Line\/|; wv\)/i.test(ua);
}

/**
 * Steg för steg, för den plattform användaren faktiskt sitter på.
 *
 * @param siteAllowed true när webbläsaren redan tillåter sidan. Då sitter
 * spärren längre ut, och råd om sidans egna inställningar hjälper inte.
 */
export function locationHelp(siteAllowed = false) {
  if (isInAppBrowser()) {
    return {
      title: 'Öppna i din vanliga webbläsare',
      steps: [
        'Sidan visas inuti en annan app, och där brukar platsåtkomst vara avstängd.',
        'Tryck på menyn (⋯) och välj "Öppna i webbläsare".',
      ],
    };
  }

  if (siteAllowed) {
    switch (detectPlatform()) {
      case 'ios':
        return {
          title: 'Platstjänster är avstängda',
          steps: [
            'Sidan har redan tillstånd — spärren sitter i telefonen.',
            'Inställningar → Integritet och säkerhet → Platstjänster: slå på.',
            'Kontrollera att Safari står på "När appen används".',
          ],
        };
      case 'android':
        return {
          title: 'Platstjänster är avstängda',
          steps: [
            'Sidan har redan tillstånd — spärren sitter i telefonen.',
            'Inställningar → Plats: slå på.',
            'Inställningar → Appar → Chrome → Behörigheter → Plats: tillåt.',
          ],
        };
      default:
        return {
          title: 'Platstjänster är avstängda',
          steps: [
            'Sidan har redan tillstånd — spärren sitter i datorn.',
            'Windows: Inställningar → Sekretess och säkerhet → Plats: slå på, och tillåt skrivbordsappar.',
            'Mac: Systeminställningar → Integritet och säkerhet → Platstjänster: tillåt din webbläsare.',
          ],
        };
    }
  }

  switch (detectPlatform()) {
    case 'ios':
      return {
        title: 'Slå på platsåtkomst i Safari',
        steps: [
          'Tryck på aA i adressfältet → Inställningar för webbplats → Plats → Tillåt.',
          'Kontrollera även Inställningar → Appar → Safari → Plats.',
          'Platstjänster måste vara på: Inställningar → Integritet och säkerhet → Platstjänster.',
        ],
      };
    case 'android':
      // Ordningen gar utifran och in. Star sidan varken under "Tillats" eller
      // "Tillats ej" finns inget sparat nej att ta bort — da kommer nekandet
      // fran Android, inte fran Chrome. Chromes platssida har inget
      // huvudreglage; de tre alternativen dar styr bara hur fragor visas.
      return {
        title: 'Chrome saknar troligen platsbehörighet',
        steps: [
          'Android: Inställningar → Appar → Chrome → Behörigheter → Plats → Tillåt. Saknar Chrome själv behörighet nekas alla sidor, och ingen enskild sida syns som blockerad.',
          'Android: Inställningar → Plats — platstjänster måste vara på.',
          'Kontrollera listorna: Chrome ⋮ → Inställningar → Webbplatsinställningar → Plats. Ligger cruze.se under "Tillåts ej", öppna den och välj Rensa och återställ.',
          'Ladda om sidan och tryck på "Min position" — då kommer frågan.',
        ],
      };

    default:
      return {
        title: 'Slå på platsåtkomst i webbläsaren',
        steps: [
          'Klicka på hänglåset i adressfältet och tillåt plats för cruze.se.',
          'Ladda sedan om sidan.',
        ],
      };
  }
}

/**
 * Förbjuder sidans egna svarshuvuden platsåtkomst?
 *
 * En Permissions-Policy med geolocation=() stänger av platsen for alla,
 * sidan själv inräknad. Då svarar webbläsaren "nekad" utan att fråga och
 * utan att spara något — telefonens inställningar spelar ingen roll. Det
 * gick att leta efter i dagar i fel ände; nu säger appen det direkt.
 */
export function blockedByPolicy() {
  const policy = document.permissionsPolicy || document.featurePolicy;
  if (!policy || typeof policy.allowsFeature !== 'function') return false;
  try {
    return !policy.allowsFeature('geolocation');
  } catch {
    return false;
  }
}

/**
 * Webbläsarens nuvarande tillstånd, om det går att läsa.
 * Ger 'unknown' där Permissions API saknas — då får ett försök ge svaret.
 */
export async function readLocationPermission() {
  if (!navigator.permissions || !navigator.permissions.query) return 'unknown';
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' });
    return status.state;
  } catch {
    return 'unknown';
  }
}
